import React, { useState, useEffect, useRef } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { useSelector, useDispatch } from 'react-redux';
import { FaToggleOn, FaToggleOff, FaUserAlt, FaSignOutAlt, FaBars, FaTimes, FaBell } from 'react-icons/fa';
import { toast } from 'react-toastify';
import { logoutDriver, clearDriverAuth } from '../features/driver/driverAuthSlice';
import { updateDriverAvailability, getDriverProfile } from '../features/driver/driverSlice';
import socketService from '../utils/socketService';
import { isObjectId } from '../utils/objectIdUtils';

const DriverHeader = () => {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [pendingRidesCount, setPendingRidesCount] = useState(0);
  const dropdownRef = useRef(null);

  const navigate = useNavigate();
  const dispatch = useDispatch();
  const location = useLocation();
  
  // Listen for pending rides count from DriverDashboard
  useEffect(() => {
    const handler = (e) => setPendingRidesCount(e.detail?.count || 0);
    window.addEventListener('pendingRidesUpdate', handler);
    return () => window.removeEventListener('pendingRidesUpdate', handler);
  }, []);

  // Get driver data from both auth and profile state
  const { driver: driverAuth } = useSelector((state) => state.driverAuth);
  const { driver: driverData } = useSelector((state) => state.driver);
  
  // Ensure driver profile is loaded and refresh periodically to get latest availability status
  useEffect(() => {
    if (driverAuth && !driverData) {
      const driverId = driverAuth._id || (driverAuth.driver && driverAuth.driver._id);
      if (driverId && isObjectId(String(driverId))) {
        dispatch(getDriverProfile(driverId));
      }
    }
  }, [driverAuth, driverData, dispatch]);

  // Handle click outside to close dropdown
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsDropdownOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  // Refresh driver profile every 30 seconds to ensure availability status is current
  useEffect(() => {
    if (driverAuth) {
      const driverId = driverAuth._id || (driverAuth.driver && driverAuth.driver._id);
      if (driverId && isObjectId(String(driverId))) {
        const interval = setInterval(() => {
          dispatch(getDriverProfile(driverId));
        }, 30000); // Refresh every 30 seconds

        return () => clearInterval(interval);
      }
    }
  }, [driverAuth, dispatch]);

  // Listen for ride completion events to immediately update driver status
  useEffect(() => {
    if (driverAuth) {
      const driverId = driverAuth._id || (driverAuth.driver && driverAuth.driver._id);
      if (driverId && isObjectId(String(driverId))) {
        // Connect to socket
        socketService.connect();

        // Listen for ride completion events
        socketService.onRideCompleted((data) => {
          if (data.driverId === driverId) {
            // Immediately refresh driver profile to get updated availability status
            dispatch(getDriverProfile(driverId));
          }
        });

        return () => {
          // Don't disconnect socket - it's shared across pages
        };
      }
    }
  }, [driverAuth, dispatch]);
  
  // Get the driver name from any available source
  const driverName = driverData?.name || driverAuth?.name || driverAuth?.driver?.name || 'Driver';

  // Debug logging to track driver data sources and validate driver ID
  useEffect(() => {

    // Validate driver ID format and clear session if invalid
    if (driverData?._id) {
      const driverId = String(driverData._id);
      if (!isObjectId(driverId)) {
        console.error('❌ Invalid driver ID format detected:', driverId);
        dispatch(clearDriverAuth());
        navigate('/driver/login', { replace: true });
      }
    }
  }, [driverAuth, driverData, driverName, dispatch, navigate]);
  
  // Function to toggle driver availability status
  const toggleAvailability = async () => {
    // Get driver ID from available sources (prioritize driverData, fallback to driverAuth)
    const driverId = driverData?._id || driverAuth?._id || driverAuth?.driver?._id;

    if (!driverId) {
      console.error('❌ Cannot toggle availability: No driver ID found', {
        driverData: !!driverData,
        driverDataId: driverData?._id,
        driverAuth: !!driverAuth,
        driverAuthId: driverAuth?._id,
        driverAuthDriverId: driverAuth?.driver?._id
      });
      toast.error('Unable to update availability. Please refresh the page.');
      return;
    }

    // Validate driver ID format
    if (!isObjectId(String(driverId))) {
      console.error('❌ Invalid driver ID format:', driverId);
      toast.error('Invalid driver session. Please log in again.');
      dispatch(clearDriverAuth());
      navigate('/driver/login');
      return;
    }

    const currentAvailability = driverData?.isAvailable ?? false;

  
    setLoading(true);
    try {
      const result = await dispatch(updateDriverAvailability({
        driverId,
        isAvailable: !currentAvailability
      }));

      if (result.type.endsWith('/rejected')) {
        console.error('❌ Failed to update availability:', result.payload);

        // Handle specific error cases
        if (result.payload && result.payload.includes('Driver not found')) {
          // Vendor drivers may intermittently return 'Driver not found' while backend creates records.
          // Treat as non-fatal: retry profile fetch silently and show a gentle warning instead of logging out.
          console.warn('Driver not found when toggling availability. Will retry fetching profile.');
          toast.warn('Temporary issue updating availability — retrying silently.');
          // Retry profile fetch after a short delay
          setTimeout(() => {
            const driverId = driverAuth?._id || (driverAuth && driverAuth.driver && driverAuth.driver._id);
            if (driverId) dispatch(getDriverProfile(driverId));
          }, 5000);
        } else if (result.payload && result.payload.includes('No authentication token')) {
          toast.error('Session expired. Please log in again.');
          dispatch(clearDriverAuth());
          navigate('/driver/login');
        } else {
          toast.error(result.payload || 'Failed to update availability');
        }
      } else {
        toast.success(`You are now ${!currentAvailability ? 'available' : 'unavailable'} for rides`);
      }
    } catch (error) {
      console.error('❌ Error toggling availability:', error);
      toast.error('An error occurred while updating availability');
    } finally {
      setLoading(false);
    }
  };

  const handleLogout = () => {

    dispatch(clearDriverAuth()); // Use clearDriverAuth instead of logoutDriver for complete cleanup
    dispatch(logoutDriver());
    navigate('/driver/login');
  };

  return (
    <header className="bg-slate-900/95 backdrop-blur-md border-b border-slate-800 py-3.5 text-white shadow-lg fixed top-0 left-0 right-0 z-50">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 flex justify-between items-center">
        <div className="flex items-center gap-4">
          <Link to="/driver/dashboard" className="flex items-center gap-2 group">
            <div className="w-10 h-10 rounded-xl bg-amber-500 flex items-center justify-center shadow-md shadow-amber-500/20 group-hover:scale-105 transition-transform duration-300">
              <span className="text-slate-950 font-black text-xl">C</span>
            </div>
            <div className="flex flex-col">
              <span className="text-sm font-black tracking-wider text-white">CABZIGO</span>
              <span className="text-[10px] font-bold text-amber-500 tracking-widest uppercase">Driver Panel</span>
            </div>
          </Link>

          {driverData && driverData.isApproved && (
            <div className="ml-2 flex items-center space-x-3">
              {/* Driver Type Badge - Only for vendor drivers */}
              {driverData.isVendorDriver && (
                <div className="px-2.5 py-1 rounded-md text-[10px] font-extrabold uppercase tracking-wider bg-blue-500/10 text-blue-400 border border-blue-500/20">
                  🏢 Vendor
                </div>
              )}

              {/* Custom Availability Toggle Switch */}
              <div className="flex items-center bg-slate-800/80 border border-slate-700/50 px-3.5 py-1.5 rounded-full shadow-inner gap-2">
                <span className="text-[10px] font-extrabold uppercase tracking-wider text-slate-400">
                  Status
                </span>
                <button
                  onClick={toggleAvailability}
                  disabled={loading}
                  className={`relative inline-flex h-5 w-10 items-center rounded-full transition-colors duration-300 focus:outline-none ${
                    driverData.isAvailable ? 'bg-emerald-500 shadow-[0_0_10px_rgba(16,185,129,0.3)]' : 'bg-slate-600'
                  }`}
                  aria-label={driverData.isAvailable ? "Set unavailable" : "Set available"}
                >
                  <span
                    className={`inline-block h-3.5 w-3.5 transform rounded-full bg-white transition-transform duration-300 ${
                      driverData.isAvailable ? 'translate-x-5' : 'translate-x-1.1'
                    }`}
                  />
                </button>
                <span className={`text-[10px] font-extrabold tracking-wider transition-all duration-300 ${driverData.isAvailable ? 'text-emerald-400' : 'text-slate-400'}`}>
                  {driverData.isAvailable ? 'ONLINE' : 'OFFLINE'}
                </span>
              </div>
            </div>
          )}
        </div>
        
        <div className="flex items-center gap-4">
          <div className="hidden md:flex items-center space-x-6">
            {/* New Ride Requests badge in top nav */}
            <Link
              to="/driver/new-ride-requests"
              className="relative flex items-center gap-1.5 text-sm font-semibold transition-colors duration-300 text-emerald-400 hover:text-emerald-300"
            >
              <span className="relative">
                <FaBell className="text-base" />
                {pendingRidesCount > 0 && (
                  <span className="absolute -top-1.5 -right-1.5 flex h-4 w-4 items-center justify-center rounded-full bg-emerald-500 text-white text-[9px] font-extrabold animate-bounce">
                    {pendingRidesCount}
                  </span>
                )}
              </span>
              New Rides
              {pendingRidesCount > 0 && (
                <span className="relative flex h-2 w-2">
                  <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-emerald-400 opacity-75" />
                  <span className="relative inline-flex rounded-full h-2 w-2 bg-emerald-500" />
                </span>
              )}
            </Link>
            <Link 
              to="/driver/current-ride" 
              className={`text-sm font-semibold transition-colors duration-300 ${
                location.pathname === "/driver/current-ride" ? "text-amber-500" : "text-slate-300 hover:text-white"
              }`}
            >
              Current Ride
            </Link>
            <Link 
              to="/driver/ride-history" 
              className={`text-sm font-semibold transition-colors duration-300 ${
                location.pathname === "/driver/ride-history" ? "text-amber-500" : "text-slate-300 hover:text-white"
              }`}
            >
              Ride History
            </Link>
            
            <div className="relative" ref={dropdownRef}>
              <button
                onClick={() => setIsDropdownOpen(!isDropdownOpen)}
                className="flex items-center gap-2 px-3 py-1.5 rounded-lg bg-slate-800/40 border border-slate-800 hover:bg-slate-800/80 transition-all duration-300"
              >
                <div className="w-6 h-6 rounded-full bg-amber-500/10 border border-amber-500/20 flex items-center justify-center text-amber-500 text-xs font-bold">
                  {driverName.charAt(0).toUpperCase()}
                </div>
                <span className="text-sm font-semibold text-slate-200">{driverName}</span>
              </button>
              
              {isDropdownOpen && (
                <div className="absolute right-0 mt-2 w-48 bg-slate-900 border border-slate-800 rounded-xl shadow-2xl py-1 z-50 animate-fadeIn">
                  <Link
                    to="/driver/profile"
                    onClick={() => setIsDropdownOpen(false)}
                    className="flex items-center px-4 py-2 text-sm text-slate-300 hover:bg-slate-800 hover:text-white"
                  >
                    <FaUserAlt className="mr-2.5 text-slate-500" /> My Profile
                  </Link>
                  <button
                    onClick={() => {
                      setIsDropdownOpen(false);
                      handleLogout();
                    }}
                    className="flex items-center w-full text-left px-4 py-2 text-sm text-slate-300 hover:bg-red-500/10 hover:text-red-400 transition-colors"
                  >
                    <FaSignOutAlt className="mr-2.5 text-slate-500" /> Logout
                  </button>
                </div>
              )}
            </div>
          </div>
          
          {/* Mobile menu button */}
          <button 
            className="md:hidden p-1.5 rounded-lg bg-slate-850 border border-slate-800 text-slate-300 hover:text-white focus:outline-none"
            onClick={() => setIsMenuOpen(!isMenuOpen)}
          >
            {isMenuOpen ? <FaTimes size={20} /> : <FaBars size={20} />}
          </button>
        </div>
      </div>
      
      {/* Mobile menu */}
      {isMenuOpen && (
        <div className="md:hidden bg-slate-900 border-b border-slate-800 absolute left-0 right-0 z-50 shadow-2xl py-3 px-4 space-y-1 animate-fadeIn">
          {/* New Ride Requests in mobile menu */}
          {pendingRidesCount > 0 && (
            <Link
              to="/driver/new-ride-requests"
              className="flex items-center gap-2 py-2 px-3 text-sm font-bold rounded-lg text-emerald-400 bg-emerald-500/10 animate-pulse"
              onClick={() => setIsMenuOpen(false)}
            >
              <FaBell />
              New Ride Requests
              <span className="ml-auto bg-emerald-500 text-white text-xs px-2 py-0.5 rounded-full font-extrabold">{pendingRidesCount}</span>
            </Link>
          )}
          <Link
            to="/driver/current-ride"
            className="block py-2 px-3 text-sm font-medium rounded-lg text-slate-300 hover:bg-slate-800 hover:text-white"
            onClick={() => setIsMenuOpen(false)}
          >
            Current Ride
          </Link>
          <Link 
            to="/driver/ride-history" 
            className="block py-2 px-3 text-sm font-medium rounded-lg text-slate-300 hover:bg-slate-800 hover:text-white"
            onClick={() => setIsMenuOpen(false)}
          >
            Ride History
          </Link>
          <Link 
            to="/driver/profile" 
            className="block py-2 px-3 text-sm font-medium rounded-lg text-slate-300 hover:bg-slate-800 hover:text-white"
            onClick={() => setIsMenuOpen(false)}
          >
            My Profile
          </Link>
          <button 
            onClick={() => {
              handleLogout();
              setIsMenuOpen(false);
            }}
            className="flex items-center w-full text-left py-2 px-3 text-sm font-medium rounded-lg text-red-400 hover:bg-red-500/10"
          >
            <FaSignOutAlt className="mr-2" /> Logout
          </button>
        </div>
      )}
    </header>
  );
};

export default DriverHeader;
